import { addYears, startOfDay, subYears } from 'date-fns';
import { parseDateOnly } from './scheduleGanttMapping';

export type ScheduleViewDateRange = { from: Date; to: Date };

/**
 * Timeline + API query span from filter fields (wide enough to load candidate tasks).
 * - Only start: from that day through +2 years.
 * - Only end: two years before end through end.
 * - Both: inclusive span, order normalized.
 */
export function getScheduleViewDateRange(startDate: string, endDate: string): ScheduleViewDateRange | null {
  const s = (startDate || '').trim();
  const e = (endDate || '').trim();
  if (!s && !e) return null;

  const fromD = s ? parseDateOnly(s) : null;
  const toD = e ? parseDateOnly(e) : null;

  if (fromD && !toD) {
    const from = startOfDay(fromD);
    return { from, to: startOfDay(addYears(from, 2)) };
  }
  if (!fromD && toD) {
    const to = startOfDay(toD);
    return { from: startOfDay(subYears(to, 2)), to };
  }
  if (fromD && toD) {
    let from = startOfDay(fromD);
    let to = startOfDay(toD);
    if (from.getTime() > to.getTime()) [from, to] = [to, from];
    return { from, to };
  }
  return null;
}

/** Legacy name — same as {@link getScheduleViewDateRange}. */
export const resolveSchedulePlannedFilterWindow = getScheduleViewDateRange;

/**
 * Filters tasks by interval overlap against the selected schedule window.
 * - taskStart = `identifiedDate` ?? `startDate`
 * - taskEnd = `plannedCompletionDate` ?? taskStart
 * A task matches when it overlaps the filter range:
 * - if start boundary exists: taskEnd must be on/after it
 * - if end boundary exists: taskStart must be on/before it
 * Year-round / as-needed rows always match when any date filter is active.
 */
export function taskMatchesSchedulePlannedDateFilters(
  task: {
    identifiedDate?: string | null;
    startDate?: string | null;
    plannedCompletionDate?: string | null;
    yearRoundSchedule?: boolean;
  },
  filterStartDate: string,
  filterEndDate: string
): boolean {
  const fs = (filterStartDate || '').trim();
  const fe = (filterEndDate || '').trim();
  if (!fs && !fe) return true;

  if (task.yearRoundSchedule) return true;

  const plannedStart = parseDateOnly(String(task.identifiedDate ?? task.startDate ?? '').trim());
  const plannedEndFromField = parseDateOnly(String(task.plannedCompletionDate ?? '').trim());
  const plannedEnd = plannedEndFromField ?? plannedStart;
  if (!plannedStart || !plannedEnd) return false;

  if (fs) {
    const boundary = parseDateOnly(fs);
    if (!boundary) return false;
    if (startOfDay(plannedEnd).getTime() < startOfDay(boundary).getTime()) return false;
  }

  if (fe) {
    const boundary = parseDateOnly(fe);
    if (!boundary) return false;
    if (startOfDay(plannedStart).getTime() > startOfDay(boundary).getTime()) return false;
  }

  return true;
}
